// utils/mapBoxHelpers.ts
import type mapboxgl from 'mapbox-gl';

/**
 * Ejecuta `callback` tan pronto como el estilo del mapa esté listo.
 * - Si ya está cargado, lo llama inmediatamente.
 * - Si no, se suscribe al evento 'style.load'.
 */
export function whenStyleReady(map: mapboxgl.Map, callback: () => void): void {
  if (map.isStyleLoaded()) {
    callback();
  } else {
    map.once('style.load', callback);
  }
}

/**
 * Runs a callback once the active Mapbox style can safely accept sources and
 * layers. Unlike a one-shot `styledata` listener, it ignores early styledata
 * events and keeps waiting until `isStyleLoaded()` confirms readiness.
 *
 * The returned cleanup prevents an obsolete React effect from drawing an old
 * collection after its input has changed.
 */
export function subscribeWhenStyleReady(map: mapboxgl.Map, callback: () => void): () => void {
  let disposed = false;

  const cleanup = () => {
    map.off('styledata', handleStyleReady);
    map.off('idle', handleStyleReady);
  };

  const handleStyleReady = () => {
    if (disposed || !map.isStyleLoaded()) {
      return;
    }
    cleanup();
    callback();
  };

  if (map.isStyleLoaded()) {
    callback();
    return cleanup;
  }

  map.on('styledata', handleStyleReady);
  map.on('idle', handleStyleReady);
  return () => {
    disposed = true;
    cleanup();
  };
}

/**
 * Añade una fuente GeoJSON de forma segura, sólo si no existe ya,
 * y siempre tras garantizar que el estilo está cargado.
 *
 * @param map    Instancia de mapbox-gl.Map
 * @param id     Identificador de la fuente
 * @param source Configuración de la fuente (AnySourceData)
 */
export function safeAddSource(map: mapboxgl.Map, id: string, source: mapboxgl.AnySourceData): void {
  whenStyleReady(map, () => {
    if (!map.getSource(id)) {
      map.addSource(id, source);
    }
  });
}

/**
 * Añade una capa de estilo de manera segura, sólo si no existe ya,
 * y siempre tras garantizar que el estilo está cargado.
 *
 * @param map   Instancia de mapbox-gl.Map
 * @param layer Definición completa de la capa (Layer)
 */
export function safeAddLayer(map: mapboxgl.Map, layer: mapboxgl.Layer): void {
  whenStyleReady(map, () => {
    if (!map.getLayer(layer.id)) {
      map.addLayer(layer);
    }
  });
}

/**
 * Mueve una capa por encima de otra (o al tope si no se indica `beforeId`),
 * siempre tras garantizar que el estilo está cargado.
 *
 * @param map      Instancia de mapbox-gl.Map
 * @param layerId  ID de la capa que queremos mover
 * @param beforeId (opcional) ID de la capa antes de la cual insertar layerId
 */
export function safeMoveLayer(map: mapboxgl.Map, layerId: string, beforeId?: string): void {
  whenStyleReady(map, () => {
    try {
      if (!map.getLayer(layerId)) {
        return;
      }
      if (beforeId && map.getLayer(beforeId)) {
        map.moveLayer(layerId, beforeId);
      } else {
        map.moveLayer(layerId);
      }
    } catch (error) {
      console.warn(`safeMoveLayer: no se pudo mover la capa "${layerId}"`, error);
    }
  });
}

/**
 * Establece de forma segura una *paint-property* esperando a que el estilo
 * esté cargado y evitando excepciones si la capa aún no existe.
 */
/*───────────────────────  safeSetPaintProperty  ─────────────────────*/

/*────────────────────── GeoJSON sanitizer ──────────────────────*/
/**
 * Removes any feature whose geometry contains non‑finite coordinates
 * (null, undefined, NaN or ±Infinity).  Nested coordinate arrays
 * are traversed recursively so it works for LineString, Polygon,
 * MultiLineString, MultiPolygon, etc.
 *
 * Why?  A single NaN sneaking into a coordinate array can crash
 * Mapbox‑GL internals with cryptic errors like
 *   “undefined is not an object (evaluating 'r[0]')”
 * inside pointRayIntersection / coveringTiles.
 */

type LngLat = [number, number];
type NestedCoords = LngLat | NestedCoords[];

const isLngLat = (coords: unknown): coords is LngLat => {
  return (
    Array.isArray(coords) &&
    coords.length >= 2 &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number' &&
    Number.isFinite(coords[0]) &&
    Number.isFinite(coords[1]) &&
    Math.abs(coords[0]) <= 180 &&
    Math.abs(coords[1]) <= 90
  );
};
function sanitizeFeatureCollection<F extends GeoJSON.FeatureCollection<GeoJSON.Geometry>>(
  fc: F,
): F {
  const sanitizeCoords = (coords: unknown): NestedCoords | null => {
    if (!Array.isArray(coords)) {
      return null;
    }

    if (Array.isArray(coords[0])) {
      const cleaned = (coords as unknown[])
        .map((c) => sanitizeCoords(c))
        .filter((c): c is NestedCoords => c !== null);
      return cleaned.length > 0 ? cleaned : null;
    }

    return isLngLat(coords) ? (coords as LngLat) : null;
  };

  return {
    ...fc,
    features: fc.features
      .map((feat) => {
        const geom = feat.geometry;
        if (!geom || geom.type === 'GeometryCollection') {
          return null;
        }
        type NonCollectionGeom = Exclude<GeoJSON.Geometry, GeoJSON.GeometryCollection>;
        const nonCollection = geom;
        const cleanCoords = sanitizeCoords(nonCollection.coordinates as unknown);
        if (!cleanCoords) {
          return null;
        }
        return {
          ...feat,
          geometry: { ...nonCollection, coordinates: cleanCoords } as NonCollectionGeom,
        } as typeof feat;
      })
      .filter((f): f is (typeof fc.features)[number] => f !== null),
  } as F;
}
/*────────────────────────────────────────────────────────────────*/

/**
 * Crea «sourceId» si no existe y actualiza los datos.
 */
export const safeSetGeoJSONData = (
  map: mapboxgl.Map | null | undefined,
  sourceId: string,
  collection: GeoJSON.FeatureCollection,
) => {
  if (!map) {
    return;
  }

  // 🔎 Sanitize to prevent Mapbox crashes due to NaN/undefined coords
  const cleanCollection = sanitizeFeatureCollection(collection);

  if (map.getSource(sourceId)) {
    (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(cleanCollection);
  } else {
    safeAddSource(map, sourceId, { type: 'geojson', data: cleanCollection });
  }
};
